<template>
  <div class="relative autocomplete">
    <input
      v-model="selectedDisplay"
      type="text"
      class="block w-full leading-tight bg-white border-gray-300 rounded-sm shadow-sm border-1 focus:shadow-outline text-primary placeholder-slate-300 focus:border-main-primary focus:ring-main-primary"
      required
      :placeholder="placeholder"
      @input="onChange"
      @keydown.down="onArrowDown"
      @keydown.up="onArrowUp"
      @keydown.enter="onEnter"
      @keyup="removeResults"
    >
    <ul
      v-show="isOpen"
      id="autocomplete-results"
      class="w-full py-2 mt-2 bg-gray-100 border autocomplete-results border-slate-300"
    >
      <li
        v-if="isLoading"
        class="loading"
      >
        Loading results...
      </li>
      <template v-else>
        <li
          v-for="(result, i) in results"
          :key="i"
          class="autocomplete-result"
          :class="{ 'is-active': i === arrowCounter }"
          @click="onClick(i)"
        >
          <span class="font-medium">{{ result[displayKey] }}</span>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'AutoCompleteInput',
  props: {
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    isAsync: {
      type: Boolean,
      required: false,
      default: false,
    },
    valueKey: {
      type: String,
      required: true,
    },
    displayKey: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    model: {
      type: [String, Number, Object, Boolean, Array],
      required: false,
      default: () => {
        return {
          region_name: '',
          region_code: null,
        }
      },
    },
  },

  emits: ['input'],

  data() {
    return {
      isOpen: false,
      results: [],
      inputItems: this.items,
      selectedDisplay: this.model.region_name,
      selectedValue: this.model.region_code,
      isLoading: false,
      arrowCounter: -1,
    }
  },

  watch: {
    inputItems(value, oldValue) {
      if (value.length !== oldValue.length) {
        this.results = value
        this.isLoading = false
      }
    },
    selectedDisplay(value) {
      this.selectedValue = this.results.find(
        region => region.region_name === value,
      )
    },
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },

  unmounted() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    setResult(result) {
      this.selectedValue = result
      this.isOpen = false
    },
    filterResults() {
      this.results = this.items.filter((item) => {
        return item[this.displayKey]
          .toLowerCase()
          .includes(this.selectedDisplay.toLowerCase())
      })
    },
    onChange() {
      const region = this.items.filter(
        item =>
          item[this.displayKey].toLowerCase()
          === this.selectedDisplay.toLowerCase(),
      )
      this.$emit('input', region ? region[0] : region)
      if (this.isAsync) {
        this.isLoading = true
      }
      else {
        this.filterResults()
        this.isOpen = this.results.length > 0
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false
        this.arrowCounter = -1
      }
    },
    onArrowDown() {
      if (this.arrowCounter < this.results.length) {
        this.arrowCounter = this.arrowCounter + 1
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter = this.arrowCounter - 1
      }
    },
    onEnter() {
      this.selectedValue = this.results[this.arrowCounter][this.valueKey]
      this.selectedDisplay = this.results[this.arrowCounter][this.displayKey]
      this.$emit('input', this.results[this.arrowCounter])
      this.isOpen = false
      this.arrowCounter = -1
    },
    onClick(index) {
      this.selectedValue = this.results[index][this.valueKey]
      this.selectedDisplay = this.results[index][this.displayKey]
      this.$emit('input', this.results[index])
      this.isOpen = false
      this.arrowCounter = -1
    },

    removeResults() {
      if (this.selectedDisplay === '') {
        this.isOpen = false
        this.arrowCounter = -1
      }
    },
  },
}
</script>

<style scoped>
.autocomplete {
  position: relative;
}

.autocomplete-results {
  border: 1px solid #eeeeee;
  min-height: 40px;
  max-height: 120px;
  overflow: auto;
  margin-bottom: 10px;
}

.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 4px 20px;
}

.autocomplete-result.is-active,
.autocomplete-result:hover {
  background-color: #4aae9b;
  color: white;
}
</style>
